/**
 * QuickAddPopup
 *
 * Compact, chrome-less status window opened by the "Add to Cyrano" bookmarklet
 * as a small top-right popup. It reads the media `url` from the query string
 * and auto-submits the upload immediately on mount (no confirmation), mirroring
 * the URL-source flow of QuickAddModal. It then acts as a status indicator:
 * spinner while uploading, a green check on success (auto-closing the window),
 * or an error with a retry link and a link into the full admin.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Spinner } from '@heroui/react'

import { QueueService } from '../../gifs/services'

type Status = 'uploading' | 'success' | 'error'

export function QuickAddPopup() {
  const [searchParams] = useSearchParams()
  const url = searchParams.get('url')?.trim() ?? ''

  const [status, setStatus] = useState<Status>('uploading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  // React StrictMode double-mounts in development, which would fire the upload
  // twice. Guard the auto-submit so it only runs once per mount cycle.
  const hasSubmittedRef = useRef(false)

  const runUpload = useCallback(async () => {
    if (!url) {
      setStatus('error')
      setErrorMessage('No media URL provided.')
      return
    }

    setStatus('uploading')
    setErrorMessage('')

    try {
      // Mirror QuickAddModal's URL-source upload exactly: same payload shape
      // and the same default conversion options.
      await QueueService.queueUpload(
        {
          type: 'url',
          data: url,
        },
        {
          maxWidth: 720,
          maxHeight: 720,
          frameRate: 24,
          quality: 95,
        }
      )

      setStatus('success')
      // Close the popup shortly after success. window.close() only works for
      // script-opened windows (which the bookmarklet's window.open is); if the
      // browser ignores it, the success card simply stays visible — harmless.
      setTimeout(() => window.close(), 1500)
    } catch (error) {
      console.error('QuickAddPopup upload failed:', error)
      setStatus('error')
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to add to Cyrano'
      )
    }
  }, [url])

  useEffect(() => {
    if (hasSubmittedRef.current) return
    hasSubmittedRef.current = true
    void runUpload()
  }, [runUpload])

  const handleRetry = () => {
    void runUpload()
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-900">
      <div className="w-full max-w-[340px] rounded-xl bg-white p-5 text-center shadow-md dark:bg-slate-800">
        {status === 'uploading' && (
          <div className="flex flex-col items-center gap-3">
            <Spinner size="md" color="primary" />
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              Adding to Cyrano…
            </p>
            {url && (
              <p className="max-w-full truncate text-xs text-slate-400 dark:text-slate-500">
                {url}
              </p>
            )}
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-2">
            <div className="text-4xl text-success">✓</div>
            <p className="text-sm font-semibold text-success-600">
              Added to Cyrano
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              (this window closes itself)
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-2">
            <div className="text-4xl text-danger">✗</div>
            <p className="text-sm font-semibold text-danger-600">
              Couldn’t add to Cyrano
            </p>
            <p className="max-w-full break-words text-xs text-slate-500 dark:text-slate-400">
              {errorMessage}
            </p>
            <div className="mt-1 flex items-center gap-4 text-xs">
              <a
                href="/admin"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline"
              >
                Open Cyrano admin
              </a>
              <button
                type="button"
                onClick={handleRetry}
                className="font-medium text-primary underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
